import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Form,
  FormField,
  TextInput,
  Layer,
  Heading,
  Text,
  Anchor,
  TextArea,
  Select,
  CheckBox,
} from "grommet";

import { CheckBoxGroup } from "grommet";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { AuthContext } from "../../contexts/AuthContext";
import supportEmails from "../../assets/country_email.json";

const ContactFormModal = ({ onClose, onSubmit, username, email, country }) => {
  const [error, setError] = useState("");
  const { jwtToken } = useContext(AuthContext);
  const [actualUserId, setActualUserId] = useState(null);


  useEffect(() => {
    // 1. grab token (adjust key if yours is different)
    if (!jwtToken) return;

    // 2. decode
    let decoded;
    try {
      decoded = jwtDecode(jwtToken);
    } catch {
      console.error('Invalid JWT');
      return;
    }

    // 3. pick correct ID based on role
    const { role, user_table_id, user_id } = decoded;
    const id =
      role === 'manager'
        ? user_table_id
        : role === 'user'
          ? user_id
          : null;

    setActualUserId(id);
  }, [jwtToken]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    country: "",
    num_participants: "",
    support_type: "",
    delivery_method: "",
    message: "",
  });
  useEffect(() => {
    setFormData({
      name: username || "", // Default to an empty string if userName is undefined
      email: email || "", // Default to an empty string if email is undefined
      contact: "",
      num_participants: "",
      support_type: "",
      delivery_method: "",
      country: country || "", // Default to an empty string if country is undefined
      message: "",
    });
  }, [username, email, country]);

  const createTicketAndEmail = async (
    userId,
    type,
    subjectTemplate,
    bodyTemplate
  ) => {
    try {
      const ticketUserId = userId || '00';
      const resp = await fetch("/api/ticket/generate-ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: ticketUserId, type }),
      });
      if (!resp.ok) throw new Error(`Status ${resp.status}`);
      const { ticket_number } = await resp.json();

      const subject = encodeURIComponent(
        `${subjectTemplate} [Ticket: ${ticket_number}]`
      );
      const body = encodeURIComponent(
        `${bodyTemplate}\n\nTicket Number: ${ticket_number}`
      );
      const bcc = encodeURIComponent("hpe.skilladvisor@meteoros.in");

      window.open(
        `mailto:hpeskill.advisor@hpe.com?subject=${subject}&bcc=${bcc}&body=${body}`
      );
    } catch (err) {
      console.error("Unable to generate ticket", err);
      alert("Could not create ticket. Please try again.");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "num_participants" && value <= 0) {
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  // const handleSubmit = () => {
  //   console.log("Form data", formData);
  //   onSubmit(formData);
  //   onClose(); // Close the modal after submission
  // };
  const handleSubmit = () => {
    if (!formData.support_type) {
      setError("Please select the type of support you require.");
      return;
    }
    // Clear any previous error
    setError("");

    const now = new Date();
    const date = now.toLocaleDateString();       // e.g. "7/10/2025"
    const time = now.toLocaleTimeString();       // e.g. "3:45:20 PM"
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // merge date & time into the payload
    const payload = {
      ...formData,
      date,
      time,
      timezone,
    };

    console.log("Form data", payload);
    // Store data in the backend via the parent component
    onSubmit(payload);
    // Close the modal
    onClose();

    const bccParam = `&bcc=${encodeURIComponent("hpe.skilladvisor@meteoros.in")}`;

    // If "Custom or dedicated learning support" is selected, open a prefilled mailto link
    if (
      formData.support_type === "Custom or dedicated learning support" ||
      formData.support_type === "Assessment related support"
    ) {
      // Look up the email for the user's country, or default if not found
      const mailToAddress =
        supportEmails[formData.country] || "hpeskill.advisor@hpe.com";

      // if we have a country-specific address, CC the HPE Skills Advisor
      const shouldCcAdvisor = !!supportEmails[formData.country];
      const ccParam = shouldCcAdvisor
        ? `&cc=${encodeURIComponent("hpeskill.advisor@hpe.com")}`
        : "";

      const subject =
        formData.support_type === "Custom or dedicated learning support"
          ? "Request for Custom or Dedicated Learning Support"
          : "Request for Assessment Related Support";

      const bodyParts = [];
      bodyParts.push("Hi there,");

      if (formData.name) {
        bodyParts.push(`My name is ${formData.name}.`);
      }

      if (formData.country) {
        bodyParts.push(`I am reaching out from ${formData.country}.`);
      }

      if (formData.email) {
        bodyParts.push(`You can contact me at ${formData.email}.`);
      }

      if (formData.contact) {
        bodyParts.push(`Alternatively, call me at ${formData.contact}.`);
      }

      if (formData.support_type === "Custom or dedicated learning support") {
        if (formData.num_participants) {
          bodyParts.push(
            `I'm interested in custom or dedicated learning support for a group of ${formData.num_participants} participants.`,
          );
        }
      } else if (formData.support_type === "Assessment related support") {
        bodyParts.push(
          "I'm interested in assessment related support for my team.",
        );
      }

      const deliveryMethod = Array.isArray(formData.delivery_method)
        ? formData.delivery_method.join(", ")
        : formData.delivery_method;

      if (deliveryMethod) {
        bodyParts.push(`My preferred delivery method is ${deliveryMethod}.`);
      }

      if (formData.message) {
        bodyParts.push(`Additional details: ${formData.message}`);
      }

      bodyParts.push(`\nThank you,\n${formData.name || ""}`);

      const body = bodyParts.join("\n\n");

      // Open the default mail client with a new email having prefilled details
      const mailtoURL =
        `mailto:${mailToAddress}` +
        `?subject=${encodeURIComponent(subject)}` +
        ccParam +
        `&body=${encodeURIComponent(body)}`;

      window.location.href = mailtoURL;
    }

    if (formData.support_type === "Application related support") {
      const subject = "Request for Application Related Support";
      const bodyTemplateParts = ["Hi there,"];
      if (formData.name) bodyTemplateParts.push(`My name is ${formData.name}.`);
      if (formData.country)
        bodyTemplateParts.push(`I am reaching out from ${formData.country}.`);
      if (formData.email)
        bodyTemplateParts.push(`You can contact me at ${formData.email}.`);
      if (formData.contact)
        bodyTemplateParts.push(`Alternatively, call me at ${formData.contact}.`);
      bodyTemplateParts.push("I need assistance related to application access.");
      if (formData.message)
        bodyTemplateParts.push(`Additional message: ${formData.message}`);
      bodyTemplateParts.push(`\nThank you,\n${formData.name || ""}`);
      const bodyTemplate = bodyTemplateParts.join("\n\n");

      createTicketAndEmail(
        actualUserId,
        formData.support_type,
        subject,
        bodyTemplate
      );
    }

  };

  return (
    <Layer onEsc={onClose} onClickOutside={onClose}>
      <Box pad="medium" gap="small" width="large">
        <Heading level="3" margin="none">
          Request dedicated learning
        </Heading>
        <Text>
          {`Hello${!username || username === "unknown" ? "" : ` ${username}`}!`}{" "}
          Use this form to submit requests for custom or dedicated training.
          Simply fill out the details below, and we&apos;ll get back to you as
          soon as possible or alternatively you can contact us directly by
          choosing your country{" "}
          <Anchor
            href="https://education.hpe.com/ww/en/training/contactus.html"
            target="_blank"
          >
            here
          </Anchor>
        </Text>
        <Form>
          <Box direction="row" gap="small" width="100%">
            <Box flex>
              <FormField label="Name" required>
                <TextInput
                  name="name"
                  value={formData.name === "unknown" ? "" : formData.name}
                  onChange={handleChange}
                  disabled={!!username && username !== "unknown"}
                />
              </FormField>
            </Box>
            <Box flex>
              <FormField label="Email" required>
                <TextInput
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!!email}
                />
              </FormField>
            </Box>
          </Box>
          <Box direction="row" gap="small" width="100%">
            <Box flex>
              <FormField label="Phone number">
                <TextInput
                  name="contact"
                  type="tel"
                  value={formData.contact}
                  onChange={handleChange}
                />
              </FormField>
            </Box>
            <Box flex>
              <FormField label="Country" required>
                <TextInput
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={!!country}
                />
              </FormField>
            </Box>
          </Box>
          <Box direction="row" gap="small" width="100%">
            <Box flex>
              <FormField label="Number of participants">
                <TextInput
                  name="num_participants"
                  type="number"
                  value={formData.num_participants}
                  onChange={handleChange}
                  min={1}
                />
              </FormField>
            </Box>
            <Box flex>
              <FormField label="What kind of support do you require:" required>
                <Select
                  name="supportType"
                  placeholder="Select"
                  options={[
                    "Application related support",
                    "Assessment related support",
                    "Custom or dedicated learning support",
                  ]}
                  value={formData.support_type}
                  onChange={({ option }) =>
                    setFormData({ ...formData, support_type: option })
                  }
                />
              </FormField>
              {error && (
                <Text color="status-critical" margin={{ top: "small" }}>
                  {error}
                </Text>
              )}
            </Box>
          </Box>
          {/* <FormField label="Preferred delivery method">
            <CheckBoxGroup
              name="delivery_method"
              options={[
                "Instructor-led training",
                "Virtual instructor-led training",
              ]}
              value={formData.delivery_method}
              onChange={({ value }) =>
                setFormData({ ...formData, delivery_method: value })
              }
            />
          </FormField> */}
          <FormField label="Preferred delivery method" margin="none" pad="none">
            <Box
              direction="row"
              align="start"
              gap="xxsmall" // minimal horizontal spacing
              margin="none"
              pad="none"
            >
              {/* Left column: ILT & VILT stacked */}
              <Box direction="column" gap="none" margin="none" pad="none">
                <CheckBox
                  name="delivery_method"
                  label="Instructor-led training"
                  checked={formData.delivery_method?.includes(
                    "Instructor-led training",
                  )}
                  onChange={(event) => {
                    const { checked } = event.target;
                    const current = formData.delivery_method || [];
                    const updated = checked
                      ? [...current, "Instructor-led training"]
                      : current.filter((v) => v !== "Instructor-led training");
                    handleChange({
                      target: { name: "delivery_method", value: updated },
                    });
                  }}
                  margin="none"
                />

                <CheckBox
                  name="delivery_method"
                  label="Virtual instructor-led training"
                  checked={formData.delivery_method?.includes(
                    "Virtual instructor-led training",
                  )}
                  onChange={(event) => {
                    const { checked } = event.target;
                    const current = formData.delivery_method || [];
                    const updated = checked
                      ? [...current, "Virtual instructor-led training"]
                      : current.filter(
                        (v) => v !== "Virtual instructor-led training",
                      );
                    handleChange({
                      target: { name: "delivery_method", value: updated },
                    });
                  }}
                  margin="none"
                />
              </Box>

              {/* Right column: DL portal & note stacked */}
              <Box direction="column" gap="none" margin="none" pad="none">
                <CheckBox
                  name="delivery_method"
                  label="HPE Digital Learner private portal"
                  checked={formData.delivery_method?.includes(
                    "HPE Digital Learner private portal",
                  )}
                  onChange={(event) => {
                    const { checked } = event.target;
                    const current = formData.delivery_method || [];
                    const updated = checked
                      ? [...current, "HPE Digital Learner private portal"]
                      : current.filter(
                        (v) => v !== "HPE Digital Learner private portal",
                      );
                    handleChange({
                      target: { name: "delivery_method", value: updated },
                    });
                  }}
                  margin="none"
                />
                <Text
                  margin={{ left: "large" }}
                  size="small"
                  color="status-critical"
                >
                  * A minimum of 8 user license is required to purchase the
                  portal.
                </Text>
              </Box>
            </Box>
          </FormField>
          <FormField label="Message">
            <TextArea
              maxLength={300}
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Please provide any additional information here in under 300 characters"
            />
          </FormField>
          <Box direction="row" gap="medium" margin={{ top: "medium" }}>
            <Button label="Submit" primary onClick={handleSubmit} />
            <Button label="Cancel" onClick={onClose} />
          </Box>
        </Form>
        <Text size="small">
          For more information on how HPE manages, uses, and protects your
          information please refer to{" "}
          <Anchor
            href="https://www.hpe.com/us/en/legal/privacy.html"
            target="_blank"
          >
            HPE Privacy Statement
          </Anchor>
        </Text>
      </Box>
    </Layer>
  );
};
ContactFormModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  username: PropTypes.string,
  email: PropTypes.string,
};
export default ContactFormModal;
